import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Empty, Icon, Spin } from 'antd';
import REDUCER_KEY from 'reducers/reducer.keys';
import { Inner } from 'components/Page';
import { list, reset } from 'actions';
import { RosterVersion } from 'components/Timeline';
import config from './config';
import NotificationInput from './input';

/**
 * @description Prepare data for api
 * @param props Object object_id, collection, tag_value, start_date, expiry_date, theme
 * @return data object
 */
const handlePrepareData = ({
  type, delta, is_before, context_theme_id, context_object_id, rosterVersion
}) => ({
  type_id: type.key,
  type: {
    id: type.key,
    name: type.label
  },
  delta: delta || '0 00:00:00',
  is_before: is_before || false,
  context_theme_id,
  context_object_id: [context_object_id],
  version_id: rosterVersion
});

const mapStateToProps = state => ({
  themes: state.api.notificationTheme.data.results,
  inFlight: state.api.notificationTheme.inFlight
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadTheme() {
    const page = 0;
    const pageSize = 1;
    const filters = [{
      id: 'model',
      value: ownProps.themeName
    }];
    const sorted = [{
      id: 'model',
      desc: false
    }];

    return dispatch(list(
      REDUCER_KEY.notificationTheme.key, {
        page,
        pageSize,
        filters,
        sorted
      }
    ));
  },
  clearTheme() {
    dispatch(reset(REDUCER_KEY.notificationTheme.key));
  }
});

const Notifications = ({
  inFlight, clearTheme, loadTheme, objectId, permissions, reducers, themes, title, isRun, onSave, onDelete, onDeleteCallback
}) => {
  useEffect(() => {
    loadTheme();
    return () => {
      clearTheme();
    };
  }, []);
  if (!themes.length) {
    return (
      <div className="bhp-container">
        <Spin indicator={<Icon type="loading" spin />} tip="Loading..." spinning={inFlight}>
          <Empty
            image="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNDEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMCAxKSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgIDxlbGxpcHNlIGZpbGw9IiNGNUY1RjUiIGN4PSIzMiIgY3k9IjMzIiByeD0iMzIiIHJ5PSI3Ii8+CiAgICA8ZyBmaWxsLXJ1bGU9Im5vbnplcm8iIHN0cm9rZT0iI0Q5RDlEOSI+CiAgICAgIDxwYXRoIGQ9Ik01NSAxMi43Nkw0NC44NTQgMS4yNThDNDQuMzY3LjQ3NCA0My42NTYgMCA0Mi45MDcgMEgyMS4wOTNjLS43NDkgMC0xLjQ2LjQ3NC0xLjk0NyAxLjI1N0w5IDEyLjc2MVYyMmg0NnYtOS4yNHoiLz4KICAgICAgPHBhdGggZD0iTTQxLjYxMyAxNS45MzFjMC0xLjYwNS45OTQtMi45MyAyLjIyNy0yLjkzMUg1NXYxOC4xMzdDNTUgMzMuMjYgNTMuNjggMzUgNTIuMDUgMzVoLTQwLjFDMTAuMzIgMzUgOSAzMy4yNTkgOSAzMS4xMzdWMTNoMTEuMTZjMS4yMzMgMCAyLjIyNyAxLjMyMyAyLjIyNyAyLjkyOHYuMDIyYzAgMS42MDUgMS4wMDUgMi45MDEgMi4yMzcgMi45MDFoMTQuNzUyYzEuMjMyIDAgMi4yMzctMS4zMDggMi4yMzctMi45MTN2LS4wMDd6IiBmaWxsPSIjRkFGQUZBIi8+CiAgICA8L2c+CiAgPC9nPgo8L3N2Zz4K"
            description="Please select a record"
          />
        </Spin>
      </div>
    );
  }
  return (
    <RosterVersion.Consumer>
      {(context) => {
        const { rosterVersion } = context;
        return (
          <Inner
            canAdmin
            reducer={reducers}
            config={{
              ...config,
              query: {
                version_id: rosterVersion
              },
              list: {
                ...config.list,
                columns: config.list.columns.filter(c => !(c.isRunOnly === true && !isRun)),
                filters: {
                  context_object_id: [objectId],
                  context_theme_id: themes.length ? [themes[0].id] : [-1]
                }
              },
              detail: {
                ...config.detail,
                parent: {
                  context_object_id: objectId,
                  context_theme_id: themes.length ? themes[0].id : -1,
                  rosterVersion
                },
                component: props => (
                  <NotificationInput
                    title={title}
                    {...props}
                  />
                ),
                title
              }
            }}
            defaultSize="280px"
            onPrepareData={handlePrepareData}
            onSuccessCallback={(response, data, item, callback) => {
              onSave(response, handlePrepareData(data));
              callback();
            }}
            onDeleteCallback={(response, callback) => {
              onDelete(response);
              onDeleteCallback(response, callback);
            }}
            scroll={{
              x: 800,
              y: '100%'
            }}
            messaging={{
              save: {
                failure: () => 'Save failed',
                success: () => 'Saving was successful'
              },
              delete: {
                disabled: () => 'Delete Successful',
                failure: error => `Delete Failed. ${error}`,
                success: () => 'Delete Successful'
              }
            }}
            permissions={permissions}
          />
        );
      }}
    </RosterVersion.Consumer>
  );
};

Notifications.propTypes = {
  title: PropTypes.string,
  inFlight: PropTypes.bool,
  isRun: PropTypes.bool, // Show extra 'run' columns if true
  loadTheme: PropTypes.func,
  objectId: PropTypes.number,
  permissions: PropTypes.object,
  themes: PropTypes.array,
  reducers: PropTypes.object,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  clearTheme: PropTypes.func,
  onDeleteCallback: PropTypes.func
};

Notifications.defaultProps = {
  title: 'Default Notification',
  inFlight: false,
  isRun: false,
  loadTheme() {
  },
  objectId: null,
  permissions: {},
  themes: [],
  reducers: {
    item: REDUCER_KEY.notificationRule,
    list: REDUCER_KEY.notificationRules
  },
  onSave() {
  },
  onDelete() {
  },
  clearTheme() {
  },
  onDeleteCallback() {
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
